export interface Example {
  english: string;
  portuguese: string;
}

export interface SubTopic {
  title: string;
  description: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface Topic {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  examples: Example[];
  subtopics: SubTopic[];
  keywords?: string[];
  tips?: string;
  commonMistake?: { wrong: string; right: string; explanation: string };
  quiz: QuizQuestion[];
}

export const basicTopics: Topic[] = [
  {
    id: "verb-to-be",
    title: "Se apresentando em inglês",
    description: "Aprenda a falar quem você é, como está, sua idade e profissão usando AM, IS e ARE",
    icon: "👋",
    color: "from-purple-500/20 to-violet-600/10",
    examples: [
      { english: "I am a student.", portuguese: "Eu sou estudante." },
      { english: "You are my friend.", portuguese: "Você é meu amigo." },
      { english: "He is happy.", portuguese: "Ele está feliz." },
      { english: "She is tired.", portuguese: "Ela está cansada." },
      { english: "It is cold today.", portuguese: "Está frio hoje." },
      { english: "We are at home.", portuguese: "Nós estamos em casa." },
      { english: "They are teachers.", portuguese: "Eles são professores." },
    ],
    subtopics: [
      { title: "✅ Afirmativa", description: "Sujeito + AM/IS/ARE + informação → I am a student." },
      { title: "❌ Negativa", description: "Sujeito + AM/IS/ARE + NOT → I am not tired. She is not here." },
      { title: "❓ Pergunta", description: "AM/IS/ARE + Sujeito + informação? → Am I late? Is she your sister?" },
    ],
    tips: "💡 'I am' vira 'I'm', 'You are' vira 'You're', 'He is' vira 'He's'. Contrações são super usadas no inglês falado!",
    quiz: [
      { question: "Complete: She ___ a doctor.", options: ["am", "is", "are", "be"], correctIndex: 1, explanation: "Para She usamos IS → She is a doctor." },
      { question: "Qual a forma negativa de 'I am happy'?", options: ["I not am happy", "I am not happy", "I amn't happy", "I no am happy"], correctIndex: 1, explanation: "A negativa é: sujeito + AM/IS/ARE + NOT → I am not happy." },
      { question: "Como perguntar 'Você está cansado?' em inglês?", options: ["You are tired?", "Are you tired?", "Do you tired?", "Is you tired?"], correctIndex: 1, explanation: "Invertemos: ARE + you + tired? → Are you tired?" },
    ],
  },
  {
    id: "demonstratives",
    title: "Apontando e indicando coisas",
    description: "Use THIS, THAT, THESE e THOSE para falar de coisas perto ou longe de você",
    icon: "👆",
    color: "from-fuchsia-500/20 to-pink-600/10",
    examples: [
      { english: "This is my coffee.", portuguese: "Este é meu café. (perto)" },
      { english: "That is your car over there.", portuguese: "Aquele é seu carro ali. (longe)" },
      { english: "These are my problems.", portuguese: "Estes são meus problemas. (perto, plural)" },
      { english: "Those are good memories.", portuguese: "Aquelas são boas memórias. (longe, plural)" },
    ],
    subtopics: [
      { title: "THIS (perto, singular)", description: "Para algo perto de você → This is my phone." },
      { title: "THAT (longe, singular)", description: "Para algo longe de você → That is her house." },
      { title: "THESE (perto, plural)", description: "Para várias coisas perto → These are my books." },
      { title: "THOSE (longe, plural)", description: "Para várias coisas longe → Those are beautiful flowers." },
    ],
    quiz: [
      { question: "Algo PERTO e SINGULAR → qual usar?", options: ["That", "Those", "This", "These"], correctIndex: 2, explanation: "THIS = perto + singular → This is my phone." },
      { question: "Complete: ___ are my friends over there.", options: ["This", "These", "That", "Those"], correctIndex: 3, explanation: "THOSE = longe + plural → Those are my friends over there." },
      { question: "'Estes livros são meus' em inglês:", options: ["This books are mine", "Those books are mine", "These books are mine", "That books are mine"], correctIndex: 2, explanation: "THESE = perto + plural → These books are mine." },
    ],
  },
  {
    id: "there-is-are",
    title: "Dizendo o que existe em um lugar",
    description: "Use THERE IS e THERE ARE para falar que algo existe ou que tem algo em algum lugar",
    icon: "📍",
    color: "from-indigo-500/20 to-blue-600/10",
    examples: [
      { english: "There is a book on the table.", portuguese: "Tem um livro na mesa." },
      { english: "There are two dogs in the yard.", portuguese: "Tem dois cachorros no quintal." },
      { english: "There is not a problem.", portuguese: "Não tem nenhum problema." },
      { english: "Is there a bathroom here?", portuguese: "Tem banheiro aqui?" },
      { english: "Are there any questions?", portuguese: "Tem alguma pergunta?" },
    ],
    subtopics: [
      { title: "THERE IS (singular)", description: "Para UMA coisa → There is a cat on the sofa." },
      { title: "THERE ARE (plural)", description: "Para VÁRIAS coisas → There are 3 people here." },
      { title: "Negativa e Pergunta", description: "There is NOT... / Is there...? / Are there...?" },
    ],
    tips: "💡 Em português dizemos 'Tem 3 cachorros'. Em inglês NÃO usamos HAVE para isso. Usamos THERE ARE!",
    quiz: [
      { question: "Complete: ___ three cats in the house.", options: ["There is", "There are", "Have", "It has"], correctIndex: 1, explanation: "Three cats = plural → There ARE three cats." },
      { question: "'Tem um banco aqui perto?' em inglês:", options: ["Has a bank near here?", "Have a bank near here?", "Is there a bank near here?", "There is a bank near here?"], correctIndex: 2, explanation: "Pergunta: IS THERE + singular → Is there a bank near here?" },
      { question: "Qual frase está ERRADA?", options: ["There is a problem.", "There are many people.", "There have two dogs.", "There is no time."], correctIndex: 2, explanation: "NÃO usamos HAVE para dizer que algo existe. O correto é There ARE two dogs." },
    ],
  },
  {
    id: "prepositions",
    title: "Onde as coisas estão e quando acontecem",
    description: "Preposições de lugar (in, on, at), tempo (at, on, in) e movimento (to, from, into)",
    icon: "📌",
    color: "from-cyan-500/20 to-teal-600/10",
    examples: [
      { english: "The keys are in the bag.", portuguese: "As chaves estão na bolsa." },
      { english: "The phone is on the table.", portuguese: "O celular está na mesa." },
      { english: "I am at home.", portuguese: "Eu estou em casa." },
      { english: "I wake up at 7 a.m.", portuguese: "Eu acordo às 7h." },
      { english: "I work on Monday.", portuguese: "Eu trabalho na segunda." },
      { english: "I was born in 1995.", portuguese: "Eu nasci em 1995." },
      { english: "She walked into the room.", portuguese: "Ela entrou na sala." },
    ],
    subtopics: [
      { title: "Lugar: IN, ON, AT", description: "IN = dentro (in the bag) · ON = sobre (on the table) · AT = ponto (at home)" },
      { title: "Tempo: AT, ON, IN", description: "AT = horas (at 7am) · ON = dias (on Monday) · IN = meses/anos (in January)" },
      { title: "Movimento: TO, FROM, INTO", description: "TO = direção (to work) · FROM = origem (from Brazil) · INTO = para dentro (into the room)" },
      { title: "Posição: UNDER, ABOVE, NEXT TO", description: "Under = embaixo · Above = acima · Next to = ao lado · Behind = atrás" },
    ],
    quiz: [
      { question: "Complete: I was born ___ 1995.", options: ["at", "on", "in", "to"], correctIndex: 2, explanation: "Para ANOS usamos IN → I was born in 1995." },
      { question: "Complete: The book is ___ the table.", options: ["in", "on", "at", "to"], correctIndex: 1, explanation: "ON = sobre uma superfície → on the table." },
      { question: "Complete: I arrive ___ work at 8.", options: ["in", "on", "at", "into"], correctIndex: 2, explanation: "AT = ponto específico → at work, at home, at school." },
    ],
  },
  {
    id: "simple-present",
    title: "Descrevendo sua rotina e hábitos",
    description: "Fale sobre o que você faz todo dia: trabalho, estudos, gostos e rotina diária",
    icon: "🔄",
    color: "from-emerald-500/20 to-green-600/10",
    examples: [
      { english: "I work every day.", portuguese: "Eu trabalho todo dia." },
      { english: "I wake up at 7.", portuguese: "Eu acordo às 7." },
      { english: "She works here.", portuguese: "Ela trabalha aqui." },
      { english: "He plays soccer.", portuguese: "Ele joga futebol." },
      { english: "I do not like coffee.", portuguese: "Eu não gosto de café." },
      { english: "Do you work here?", portuguese: "Você trabalha aqui?" },
      { english: "Does he like pizza?", portuguese: "Ele gosta de pizza?" },
    ],
    subtopics: [
      { title: "✅ Afirmativa", description: "Sujeito + verbo → I work. She works (com S para he/she/it!)" },
      { title: "❌ Negativa", description: "Sujeito + DO NOT / DOES NOT + verbo → I don't like. She doesn't live." },
      { title: "❓ Pergunta", description: "DO / DOES + sujeito + verbo? → Do you work? Does he play?" },
    ],
    tips: "⚠️ Para HE/SHE/IT: adicione -S no verbo (She works). Mas na negativa e pergunta com DOES, NÃO use o S!",
    quiz: [
      { question: "Complete: She ___ to school every day.", options: ["go", "goes", "going", "goed"], correctIndex: 1, explanation: "He/She/It leva -S → She goes." },
      { question: "Qual a negativa correta?", options: ["She don't like it.", "She doesn't likes it.", "She doesn't like it.", "She not like it."], correctIndex: 2, explanation: "DOES NOT (doesn't) + verbo SEM S → She doesn't like it." },
      { question: "Como perguntar 'Ele joga futebol?'", options: ["He plays soccer?", "Do he play soccer?", "Does he plays soccer?", "Does he play soccer?"], correctIndex: 3, explanation: "DOES + he + verbo base (sem S) → Does he play soccer?" },
    ],
  },
  {
    id: "simple-past",
    title: "Contando o que aconteceu no passado",
    description: "Conte histórias, fale do que fez ontem e relate experiências que já terminaram",
    icon: "📖",
    color: "from-amber-500/20 to-orange-600/10",
    examples: [
      { english: "I worked yesterday.", portuguese: "Eu trabalhei ontem." },
      { english: "She studied a lot.", portuguese: "Ela estudou muito." },
      { english: "I went to the gym.", portuguese: "Eu fui à academia." },
      { english: "We ate pizza last night.", portuguese: "Nós comemos pizza ontem à noite." },
      { english: "I did not work yesterday.", portuguese: "Eu não trabalhei ontem." },
      { english: "Did you study?", portuguese: "Você estudou?" },
    ],
    subtopics: [
      { title: "Verbos regulares (-ed)", description: "work → worked · study → studied · play → played" },
      { title: "Verbos irregulares", description: "go → went · eat → ate · see → saw · have → had · make → made" },
      { title: "Negativa e Pergunta", description: "DID NOT (didn't) + verbo base · DID + sujeito + verbo base?" },
    ],
    tips: "💡 Decore os irregulares mais comuns primeiro: go→went, have→had, see→saw, do→did, make→made, take→took",
    quiz: [
      { question: "Qual o passado de 'go'?", options: ["goed", "gone", "went", "going"], correctIndex: 2, explanation: "Go é irregular: go → went → gone." },
      { question: "Complete: I ___ pizza last night.", options: ["eat", "eated", "ate", "eaten"], correctIndex: 2, explanation: "Eat é irregular: eat → ate → eaten." },
      { question: "Qual a negativa correta?", options: ["I didn't went.", "I didn't go.", "I not went.", "I don't went."], correctIndex: 1, explanation: "DID NOT + verbo BASE → I didn't go (não 'went')." },
    ],
  },
  {
    id: "future",
    title: "Falando sobre planos e o futuro",
    description: "Use WILL para decisões rápidas e promessas, e GOING TO para planos já pensados",
    icon: "🚀",
    color: "from-rose-500/20 to-red-600/10",
    examples: [
      { english: "I will help you.", portuguese: "Eu vou te ajudar. (decisão agora)" },
      { english: "I will call you.", portuguese: "Eu vou te ligar. (promessa)" },
      { english: "I am going to travel.", portuguese: "Eu vou viajar. (plano)" },
      { english: "She is going to study tonight.", portuguese: "Ela vai estudar hoje à noite. (plano)" },
    ],
    subtopics: [
      { title: "WILL = decisão do momento", description: "Quando você decide NA HORA → I will help you!" },
      { title: "GOING TO = plano já feito", description: "Quando já planejou antes → I'm going to travel next month." },
      { title: "Negativa e Pergunta", description: "I will NOT (won't)... · Will you...? · Are you going to...?" },
    ],
    quiz: [
      { question: "Você já planejou viajar mês que vem. Como diz?", options: ["I will travel next month.", "I am going to travel next month.", "I travel next month.", "I traveling next month."], correctIndex: 1, explanation: "Plano já feito = GOING TO → I am going to travel." },
      { question: "Alguém derruba algo e você decide ajudar NA HORA:", options: ["I'm going to help you.", "I help you.", "I will help you.", "I helped you."], correctIndex: 2, explanation: "Decisão do momento = WILL → I will help you!" },
      { question: "Qual a negativa de 'I will go'?", options: ["I willn't go.", "I won't go.", "I will not go.", "I won't go. / I will not go."], correctIndex: 3, explanation: "Ambas estão corretas: I won't go = I will not go." },
    ],
  },
  {
    id: "modal-verbs",
    title: "Pedindo, sugerindo e expressando habilidades",
    description: "CAN, COULD, MAY, MUST, SHOULD — verbos especiais para permissão, conselho e obrigação",
    icon: "💪",
    color: "from-violet-500/20 to-purple-600/10",
    examples: [
      { english: "I can drive.", portuguese: "Eu sei dirigir. (habilidade)" },
      { english: "Can I help you?", portuguese: "Posso te ajudar? (permissão)" },
      { english: "Could you help me?", portuguese: "Poderia me ajudar? (pedido educado)" },
      { english: "May I come in?", portuguese: "Posso entrar? (formal)" },
      { english: "You must wear a seatbelt.", portuguese: "Você deve usar cinto. (obrigação)" },
      { english: "You should study more.", portuguese: "Você deveria estudar mais. (conselho)" },
      { english: "I have to work today.", portuguese: "Eu tenho que trabalhar hoje. (obrigação externa)" },
    ],
    subtopics: [
      { title: "CAN / CAN'T", description: "Habilidade e permissão informal → I can swim. Can I sit here?" },
      { title: "COULD / MAY", description: "Pedidos educados e permissão formal → Could you wait? May I leave?" },
      { title: "MUST / HAVE TO", description: "Obrigação → Must = regra/certeza · Have to = obrigação externa" },
      { title: "SHOULD / OUGHT TO", description: "Conselho → You should drink water. You ought to rest." },
    ],
    tips: "⚠️ Modal verbs NUNCA levam -S, NUNCA usam TO depois (exceto ought to), e a pergunta é feita invertendo!",
    quiz: [
      { question: "Qual é mais educado para pedir ajuda?", options: ["Can you help?", "Help me!", "Could you help me?", "You help me."], correctIndex: 2, explanation: "COULD é mais educado que CAN para pedidos → Could you help me?" },
      { question: "Complete: You ___ wear a seatbelt. (obrigação)", options: ["should", "can", "must", "could"], correctIndex: 2, explanation: "MUST = obrigação forte/regra → You must wear a seatbelt." },
      { question: "Qual está ERRADO?", options: ["She can swim.", "He should study.", "She cans drive.", "I must go."], correctIndex: 2, explanation: "Modal verbs NUNCA levam -S! O correto é 'She can drive'." },
    ],
  },
  {
    id: "question-words",
    title: "Fazendo todo tipo de pergunta",
    description: "WHAT, WHERE, WHEN, WHY, WHO, HOW — todas as question words que você precisa",
    icon: "❓",
    color: "from-sky-500/20 to-blue-600/10",
    examples: [
      { english: "What do you want?", portuguese: "O que você quer?" },
      { english: "Where are you?", portuguese: "Onde você está?" },
      { english: "When is your birthday?", portuguese: "Quando é seu aniversário?" },
      { english: "Why are you late?", portuguese: "Por que você está atrasado?" },
      { english: "Who is she?", portuguese: "Quem é ela?" },
      { english: "How is your day?", portuguese: "Como está seu dia?" },
      { english: "How much does it cost?", portuguese: "Quanto custa?" },
    ],
    subtopics: [
      { title: "WHAT / WHICH", description: "What = o quê (geral) · Which = qual (entre opções)" },
      { title: "WHERE / WHEN / WHY", description: "Where = onde · When = quando · Why = por quê" },
      { title: "WHO / WHOSE", description: "Who = quem · Whose = de quem (Whose bag is this?)" },
      { title: "HOW + variações", description: "How much (preço) · How many (quantidade) · How old (idade) · How long (tempo)" },
    ],
    quiz: [
      { question: "'Quanto custa?' em inglês:", options: ["How many costs?", "How much does it cost?", "What is the cost?", "How cost is it?"], correctIndex: 1, explanation: "HOW MUCH para preço → How much does it cost?" },
      { question: "'De quem é essa bolsa?' em inglês:", options: ["Who bag is this?", "Who's bag is this?", "Whose bag is this?", "Whom bag is this?"], correctIndex: 2, explanation: "WHOSE = de quem → Whose bag is this?" },
      { question: "Complete: ___ old are you?", options: ["What", "Which", "How", "Who"], correctIndex: 2, explanation: "HOW OLD = qual a idade → How old are you?" },
    ],
  },
];

export const advancedTopics: Topic[] = [
  {
    id: "present-perfect",
    title: "Quando o passado importa no presente",
    description: "Fale de experiências de vida, ações recentes e coisas que começaram no passado e continuam",
    icon: "🌟",
    color: "from-purple-500/20 to-violet-600/10",
    examples: [
      { english: "I have traveled abroad.", portuguese: "Eu já viajei para fora. (experiência)" },
      { english: "I have lived here for 10 years.", portuguese: "Eu moro aqui há 10 anos. (continua)" },
      { english: "I have just finished my homework.", portuguese: "Acabei de terminar minha lição. (recente)" },
      { english: "Have you ever eaten sushi?", portuguese: "Você já comeu sushi? (experiência)" },
    ],
    subtopics: [
      { title: "Experiências de vida", description: "Have you EVER...? I have NEVER... → já/nunca na vida" },
      { title: "Algo recente com impacto", description: "I have JUST finished. She has ALREADY left. → acabou de acontecer" },
      { title: "Passado que continua", description: "I have lived here FOR 10 years / SINCE 2015. → ainda continua" },
    ],
    keywords: ["already", "yet", "just", "ever", "never", "for", "since"],
    commonMistake: {
      wrong: "I did travel to the USA.",
      right: "I have traveled to the USA.",
      explanation: "Quando falamos de experiência de vida (sem dizer QUANDO), usamos HAVE + particípio!",
    },
    tips: "💡 FOR = duração (for 3 years) · SINCE = ponto de início (since 2020)",
    quiz: [
      { question: "Complete: I ___ never ___ sushi.", options: ["did / eat", "have / eaten", "has / eat", "was / eating"], correctIndex: 1, explanation: "HAVE + NEVER + particípio → I have never eaten sushi." },
      { question: "FOR ou SINCE? 'I have lived here ___ 2015.'", options: ["for", "since", "from", "at"], correctIndex: 1, explanation: "SINCE = ponto de início → since 2015." },
      { question: "Qual frase está correta?", options: ["I have went to Paris.", "I have go to Paris.", "I have been to Paris.", "I have going to Paris."], correctIndex: 2, explanation: "HAVE + particípio passado → I have been to Paris." },
    ],
  },
  {
    id: "present-perfect-continuous",
    title: "Mostrando que algo vem acontecendo",
    description: "Foque no PROCESSO e na DURAÇÃO de algo que começou no passado e continua",
    icon: "⏳",
    color: "from-fuchsia-500/20 to-pink-600/10",
    examples: [
      { english: "I have been studying all morning.", portuguese: "Estou estudando a manhã toda." },
      { english: "She has been working too much.", portuguese: "Ela tem trabalhado demais." },
      { english: "It has been raining for hours.", portuguese: "Está chovendo há horas." },
    ],
    subtopics: [
      { title: "Estrutura", description: "HAVE/HAS + BEEN + verbo-ING → I have been working." },
      { title: "Foco no processo", description: "Mostra duração, esforço ou algo temporário que ainda acontece" },
      { title: "Perfect vs Continuous", description: "I have studied (resultado ✅) vs I have been studying (processo 🔄)" },
    ],
    quiz: [
      { question: "Complete: She ___ ___ ___ for 3 hours.", options: ["has been studying", "have been study", "has been study", "have studying"], correctIndex: 0, explanation: "HAS (she) + BEEN + verbo-ING → She has been studying." },
      { question: "Qual foca no PROCESSO?", options: ["I have read 3 books.", "I have been reading all day.", "I read a book.", "I will read."], correctIndex: 1, explanation: "Present Perfect Continuous foca no processo/duração → have been reading." },
      { question: "'Está chovendo há 2 horas' em inglês:", options: ["It rains for 2 hours.", "It is raining for 2 hours.", "It has been raining for 2 hours.", "It rained for 2 hours."], correctIndex: 2, explanation: "Começou e continua = Present Perfect Continuous → It has been raining for 2 hours." },
    ],
  },
  {
    id: "past-perfect",
    title: "O passado antes do passado",
    description: "Quando duas coisas aconteceram no passado e você precisa dizer qual veio PRIMEIRO",
    icon: "⏪",
    color: "from-indigo-500/20 to-blue-600/10",
    examples: [
      { english: "I had already eaten when she arrived.", portuguese: "Eu já tinha comido quando ela chegou." },
      { english: "They had left before the movie started.", portuguese: "Eles tinham saído antes do filme começar." },
    ],
    subtopics: [
      { title: "Estrutura", description: "HAD + particípio passado → I had eaten, She had left" },
      { title: "Quando usar", description: "Histórias, explicações e arrependimentos sobre o passado" },
      { title: "Ordem dos eventos", description: "1º evento = HAD + pp · 2º evento = Simple Past" },
    ],
    commonMistake: {
      wrong: "I ate when she arrived. (mesma hora?)",
      right: "I had already eaten when she arrived. (comi ANTES)",
      explanation: "Sem Past Perfect, parece que as duas ações aconteceram ao mesmo tempo!",
    },
    quiz: [
      { question: "O que aconteceu PRIMEIRO? 'I had eaten when she arrived.'", options: ["She arrived", "I ate", "Both at the same time", "Neither"], correctIndex: 1, explanation: "HAD + particípio = ação que veio PRIMEIRO → eu comi antes dela chegar." },
      { question: "Complete: They ___ already ___ when I called.", options: ["have / left", "had / left", "has / leave", "did / left"], correctIndex: 1, explanation: "HAD + particípio passado → They had already left." },
      { question: "Qual usa Past Perfect corretamente?", options: ["I had went home.", "I had gone home before it rained.", "I had go home.", "I has gone home."], correctIndex: 1, explanation: "HAD + particípio (gone, não went!) → I had gone home." },
    ],
  },
  {
    id: "conditionals",
    title: "E se...? Situações reais e imaginárias",
    description: "IF CLAUSES — das verdades universais aos arrependimentos do passado",
    icon: "🤔",
    color: "from-cyan-500/20 to-teal-600/10",
    examples: [
      { english: "If you heat ice, it melts.", portuguese: "Se esquentar gelo, ele derrete. (sempre)" },
      { english: "If it rains, I will stay home.", portuguese: "Se chover, eu vou ficar em casa. (possível)" },
      { english: "If I were rich, I would travel.", portuguese: "Se eu fosse rico, viajaria. (imaginário)" },
      { english: "If I had studied, I would have passed.", portuguese: "Se eu tivesse estudado, teria passado. (arrependimento)" },
    ],
    subtopics: [
      { title: "Zero — Verdades gerais", description: "If + present, present → If you heat ice, it melts." },
      { title: "First — Possível e real", description: "If + present, WILL → If it rains, I will stay." },
      { title: "Second — Imaginário agora", description: "If + past, WOULD → If I were rich... (use WERE para todos!)" },
      { title: "Third — Arrependimento", description: "If + HAD + pp, WOULD HAVE + pp → If I had studied..." },
    ],
    quiz: [
      { question: "'Se eu fosse rico, viajaria' — qual conditional?", options: ["Zero", "First", "Second", "Third"], correctIndex: 2, explanation: "Situação IMAGINÁRIA no presente = Second Conditional → If I were rich, I would travel." },
      { question: "Complete: If it ___, I will take an umbrella.", options: ["rained", "rains", "rain", "will rain"], correctIndex: 1, explanation: "First Conditional: If + PRESENT → If it rains, I will..." },
      { question: "'Se eu tivesse estudado...' = qual conditional?", options: ["First", "Second", "Third", "Zero"], correctIndex: 2, explanation: "Arrependimento do passado = Third Conditional → If I had studied..." },
    ],
  },
  {
    id: "wish-clauses",
    title: "Expressando desejos e frustrações",
    description: "Use WISH para dizer o que gostaria que fosse diferente — presente, passado ou irritação",
    icon: "✨",
    color: "from-emerald-500/20 to-green-600/10",
    examples: [
      { english: "I wish I spoke English.", portuguese: "Eu gostaria de falar inglês. (desejo presente)" },
      { english: "I wish I had studied more.", portuguese: "Eu gostaria de ter estudado mais. (arrependimento)" },
      { english: "I wish you would stop talking.", portuguese: "Eu gostaria que você parasse de falar. (irritação)" },
    ],
    subtopics: [
      { title: "WISH + past", description: "Desejo sobre o PRESENTE → I wish I spoke English. (mas não falo)" },
      { title: "WISH + past perfect", description: "Arrependimento do PASSADO → I wish I had studied." },
      { title: "WISH + would", description: "Irritação/frustração → I wish you would be quiet!" },
    ],
    quiz: [
      { question: "'Eu gostaria de ser alto' (mas não sou):", options: ["I wish I am tall.", "I wish I was tall.", "I wish I will be tall.", "I wish I be tall."], correctIndex: 1, explanation: "WISH + past para desejos no presente → I wish I was/were tall." },
      { question: "Arrependimento: 'Eu gostaria de ter ido':", options: ["I wish I went.", "I wish I go.", "I wish I had gone.", "I wish I would go."], correctIndex: 2, explanation: "WISH + past perfect para arrependimento → I wish I had gone." },
      { question: "Qual expressa IRRITAÇÃO?", options: ["I wish I had a car.", "I wish you would stop!", "I wish I were rich.", "I wish I had studied."], correctIndex: 1, explanation: "WISH + WOULD = irritação/frustração → I wish you would stop!" },
    ],
  },
  {
    id: "comparatives",
    title: "Comparando coisas e pessoas",
    description: "Use comparativos e superlativos para dizer quem é melhor, maior, mais bonito...",
    icon: "⚖️",
    color: "from-amber-500/20 to-orange-600/10",
    examples: [
      { english: "This phone is cheaper than that one.", portuguese: "Este celular é mais barato que aquele." },
      { english: "English is more useful than French.", portuguese: "Inglês é mais útil que francês." },
      { english: "This is the best day of my life.", portuguese: "Este é o melhor dia da minha vida." },
      { english: "She is the most intelligent student.", portuguese: "Ela é a estudante mais inteligente." },
    ],
    subtopics: [
      { title: "Comparativo (2 coisas)", description: "Curto: + ER (cheaper) · Longo: MORE + adj (more useful)" },
      { title: "Superlativo (grupo)", description: "Curto: THE + EST (the cheapest) · Longo: THE MOST + adj" },
      { title: "Irregulares", description: "good → better → the best · bad → worse → the worst" },
    ],
    quiz: [
      { question: "Qual o comparativo de 'good'?", options: ["gooder", "more good", "better", "best"], correctIndex: 2, explanation: "Good é irregular: good → better → the best." },
      { question: "Complete: She is ___ than her sister.", options: ["more tall", "taller", "tallest", "most tall"], correctIndex: 1, explanation: "Adjetivo curto + ER → taller than." },
      { question: "Qual o superlativo de 'beautiful'?", options: ["the beautifulest", "the most beautiful", "the more beautiful", "beautifuller"], correctIndex: 1, explanation: "Adjetivo longo: THE MOST + adj → the most beautiful." },
    ],
  },
  {
    id: "phrasal-verbs",
    title: "Phrasal verbs — o segredo do inglês real",
    description: "Verbos que mudam TOTALMENTE de significado com uma preposição. Nativos usam o tempo todo!",
    icon: "🔀",
    color: "from-rose-500/20 to-red-600/10",
    examples: [
      { english: "I wake up at 7.", portuguese: "Eu acordo às 7. (wake up = acordar)" },
      { english: "Can you turn off the lights?", portuguese: "Pode apagar as luzes? (turn off = desligar)" },
      { english: "Don't give up!", portuguese: "Não desista! (give up = desistir)" },
      { english: "I need to figure out this problem.", portuguese: "Preciso resolver esse problema. (figure out = entender)" },
      { english: "I ran out of milk.", portuguese: "Fiquei sem leite. (run out of = acabar)" },
    ],
    subtopics: [
      { title: "🏠 Rotina", description: "wake up, get up, go out, hang out, come back" },
      { title: "💼 Trabalho e estudos", description: "turn on/off, clean up, work out, fill out, set up" },
      { title: "💬 Comunicação", description: "get along, break up, bring up, calm down, hang on" },
      { title: "🧠 Pensar e descobrir", description: "figure out, find out, look up, point out, check out" },
      { title: "⚡ Problemas", description: "give up, run out, break down, pass out, end up" },
    ],
    tips: "💡 Dica de ouro: NÃO memorize lista. Memorize FRASES com contexto! O significado quase nunca é literal.",
    quiz: [
      { question: "O que significa 'give up'?", options: ["Dar algo", "Desistir", "Subir", "Entregar"], correctIndex: 1, explanation: "Give up = desistir → Don't give up!" },
      { question: "'Pode desligar a TV?' em inglês:", options: ["Can you turn off the TV?", "Can you close the TV?", "Can you shut the TV?", "Can you put off the TV?"], correctIndex: 0, explanation: "Turn off = desligar → Can you turn off the TV?" },
      { question: "O que significa 'figure out'?", options: ["Sair", "Calcular a forma", "Entender/resolver", "Imaginar"], correctIndex: 2, explanation: "Figure out = entender, resolver → I need to figure out this problem." },
    ],
  },
  {
    id: "slangs",
    title: "Gírias do inglês americano",
    description: "As expressões informais que você precisa saber para entender séries, redes sociais e conversas reais",
    icon: "🔥",
    color: "from-violet-500/20 to-purple-600/10",
    examples: [
      { english: "That's awesome!", portuguese: "Isso é incrível! (awesome = muito bom)" },
      { english: "No way!", portuguese: "Sem chance! / Não acredito!" },
      { english: "He's being salty.", portuguese: "Ele tá irritado." },
      { english: "That's cap.", portuguese: "Isso é mentira." },
      { english: "This hits different.", portuguese: "Isso impacta diferente." },
    ],
    subtopics: [
      { title: "🎭 Reações", description: "cool (legal) · awesome (incrível) · no way (sem chance) · yikes (eita)" },
      { title: "👥 Pessoas", description: "dude/bro (cara/mano) · basic (sem graça) · extra (exagerado) · salty (irritado)" },
      { title: "📱 Internet", description: "ghost (sumir) · flex (ostentar) · cringe (vergonha alheia) · low-key (meio que)" },
      { title: "💬 Dia a dia", description: "chill (relaxar) · what's up (e aí) · my bad (foi mal) · lit (animado) · piece of cake (fácil)" },
    ],
    tips: "⚠️ Slangs NÃO aparecem em inglês escolar. Mas são essenciais para entender séries, filmes e TikTok!",
    quiz: [
      { question: "O que significa 'my bad'?", options: ["Meu mau", "Foi mal / Desculpa", "Minha maldade", "Estou mal"], correctIndex: 1, explanation: "My bad = foi mal, desculpa (informal) → Sorry, my bad!" },
      { question: "'Piece of cake' significa:", options: ["Pedaço de bolo", "Muito fácil", "Muito gostoso", "Muito caro"], correctIndex: 1, explanation: "Piece of cake = muito fácil → The test was a piece of cake!" },
      { question: "Alguém sumiu sem avisar. Ele te deu um:", options: ["flex", "ghost", "cap", "cringe"], correctIndex: 1, explanation: "Ghost = sumir sem avisar → He ghosted me." },
    ],
  },
];
